import { useInterval } from '@vueuse/core';
import type { ComputedRef, Ref } from 'vue';
import { computed, ref, watch } from 'vue';

import { mapVisionAiMessagesToChatMessages } from '@/components/VisionAi/chat-panel-components/utils/mapVisionAiMessagesToChatMessages';
import {
  type ChatMessage,
  ChatMessageRole,
  type VisionAiStoreMessage,
} from '@/components/VisionAi/chat-panel-components/utils/VisionChatSidePanel.types';
import { PROGRESSIVE_UPDATE_MESSAGES } from '@/components/VisionAi/constants';

function getRandomUpdateTime() {
  return Math.floor(Math.random() * 2000) + 6000;
}

/**
 * Appends a synthetic "loading" assistant bubble to the message list while the
 * backend is processing a request.
 *
 * The bubble cycles through three phases of status text
 * (START → PROCESSING → PACKAGING) with animated trailing ellipsis. Each phase
 * advances after a randomized delay (~6–8s) to give the impression of real
 * progress.
 *
 * The loading bubble only appears when the last real message is from the user,
 * preventing a flash if the user's own message hasn't arrived from the
 * WebSocket.
 *
 * Both internal intervals (phase advancement + ellipsis animation) are paused
 * when `isLoading` is false to avoid unnecessary reactivity.
 *
 * @param storeMessages - Raw message list from `visionAiMessagesStore`.
 * @param isLoading - Whether the backend is currently processing a request.
 * @returns `messagesWithLoading` — the mapped {@link ChatMessage} list with
 *   the loading bubble appended when active.
 */
export function useProgressiveLoading(
  storeMessages: Ref<VisionAiStoreMessage[]> | ComputedRef<VisionAiStoreMessage[]>,
  isLoading: Ref<boolean> | ComputedRef<boolean>,
) {
  const loadingPhase = ref<'START' | 'PROCESSING' | 'PACKAGING'>('START');
  const loadingMessageIndex = ref(0);
  const ellipsisDots = ref(0);
  const shuffledProcessingMessages = ref<string[]>([]);
  const nextUpdateTime = ref(0);

  function resetLoadingState() {
    loadingPhase.value = 'START';
    loadingMessageIndex.value = 0;
    ellipsisDots.value = 0;
    shuffledProcessingMessages.value = [...PROGRESSIVE_UPDATE_MESSAGES.PROCESSING].sort(
      () => Math.random() - 0.5,
    );
    nextUpdateTime.value = Date.now() + getRandomUpdateTime();
  }

  const { pause: pauseProgress, resume: resumeProgress } = useInterval(100, {
    controls: true,
    immediate: false,
    callback: () => {
      const now = Date.now();
      if (now < nextUpdateTime.value) return;

      if (loadingPhase.value === 'START') {
        loadingMessageIndex.value += 1;
        if (loadingMessageIndex.value >= PROGRESSIVE_UPDATE_MESSAGES.START.length) {
          loadingPhase.value = 'PROCESSING';
          loadingMessageIndex.value = 0;
        }
      } else if (loadingPhase.value === 'PROCESSING') {
        loadingMessageIndex.value += 1;
        if (loadingMessageIndex.value >= shuffledProcessingMessages.value.length) {
          loadingPhase.value = 'PACKAGING';
          loadingMessageIndex.value = 0;
        }
      } else if (loadingPhase.value === 'PACKAGING') {
        if (loadingMessageIndex.value < PROGRESSIVE_UPDATE_MESSAGES.PACKAGING.length - 1) {
          loadingMessageIndex.value += 1;
        }
      }
      nextUpdateTime.value = now + getRandomUpdateTime();
    },
  });

  const { pause: pauseEllipsis, resume: resumeEllipsis } = useInterval(500, {
    controls: true,
    immediate: false,
    callback: () => {
      ellipsisDots.value = (ellipsisDots.value + 1) % 4;
    },
  });

  const loadingText = computed(() => {
    let baseText = '';
    if (loadingPhase.value === 'START') {
      baseText = PROGRESSIVE_UPDATE_MESSAGES.START[loadingMessageIndex.value];
    } else if (loadingPhase.value === 'PROCESSING') {
      baseText = shuffledProcessingMessages.value[loadingMessageIndex.value];
    } else {
      baseText = PROGRESSIVE_UPDATE_MESSAGES.PACKAGING[loadingMessageIndex.value];
    }
    return `${baseText}${'.'.repeat(ellipsisDots.value)}`;
  });

  watch(
    isLoading,
    (loading) => {
      if (loading) {
        resetLoadingState();
        resumeProgress();
        resumeEllipsis();
      } else {
        pauseProgress();
        pauseEllipsis();
      }
    },
    { immediate: true },
  );

  const messagesWithLoading = computed((): ChatMessage[] => {
    const mapped = mapVisionAiMessagesToChatMessages(storeMessages.value);
    const lastMessage = mapped[mapped.length - 1];
    if (isLoading.value && lastMessage?.role === ChatMessageRole.User) {
      mapped.push({
        id: '__loading__',
        role: ChatMessageRole.Assistant,
        text: loadingText.value,
        timestamp: new Date(),
        isLoading: true,
      });
    }
    return mapped;
  });

  return { messagesWithLoading };
}
